/**
 * Reportes → Créditos por cliente (`/reportes/creditos-cliente`).
 *
 * Puerto de `Reportes::creditos_por_cliente` + `Reportes_model::getDataCredit` +
 * `views/menu/reportes/creditos_por_cliente.php`.
 *
 * La pantalla del panel anterior pedía **fecha de inicio, fecha de fin, cliente** y
 * el **estado del vale** (por defecto *Facturado - Pendiente pago*) y pintaba la
 * tabla de vales con el comprobante, el producto, la cantidad, el precio, el total,
 * el conductor (DNI y nombre), el kilometraje, la placa, el vale, el personal que lo
 * registró, el CPE con el que se facturó y la retención; tenía export a Excel y a PDF.
 *
 * Reglas conservadas:
 * - Se listan los vales del grifo (`credito_venta`, `estado = 'ACTIVO'` y `total <> 0`)
 *   y, en el mismo listado, las ventas al crédito de la APP (`credit_transactions`
 *   con `status = 'active'`), como el `UNION ALL` del original.
 * - El **estado del vale** es el `tipo_credito` del original: 1 todos, 2 sin facturar
 *   (`is_settled IS NULL` / `is_invoiced = 0`), 3 facturado (`is_settled = 1` con CPE),
 *   4 facturado y pagado (`pending_amount − paidout = 0`) y 5 facturado con saldo
 *   (`> 0`); en la APP sólo aplican 1 y 2 (no expone el comprobante).
 * - Los importes del **comprobante** (saldo y retención) salen sólo en la primera
 *   línea de cada CPE, como el `ROW_NUMBER() OVER (PARTITION BY external_cpe)` con el
 *   que el original evitaba repetirlos.
 *
 * Diferencias anotadas:
 * - Se añade la **fuente** de cada línea (`CREDITO` o `APP`), el filtro por **placa** y
 *   el reparto por **cliente**; el CSV y el PDF los sirve la API.
 * - El listado no pierde la línea cuyo usuario ya no existe (el original hacía
 *   `INNER JOIN users`): el personal sale vacío y la línea sigue contando.
 * - Cuando el vale no tiene número de comprobante, la columna muestra su **serie**
 *   (`CVH1`, `C001`…) en vez de quedar vacía.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosCreditosCliente,
  useCreditosCliente,
  type FiltrosCreditosCliente,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto del reporte: el año en curso. */
function rangoPorDefecto(): FiltrosCreditosCliente {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-01-01`,
    hasta: fechaLocal(hoy),
    numero_doc: '',
    // El original abría en «Facturado - Pendiente pago».
    estado: 5,
    placa: '',
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

export function ReporteCreditosClientePage() {
  useDocumentTitle('Reportes · Créditos por cliente');

  const [filtros, setFiltros] = useState<FiltrosCreditosCliente>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosCreditosCliente();
  const consulta = useCreditosCliente(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const filas = useMemo(() => datos?.filas ?? [], [datos]);
  // El listado se pagina en el navegador; el pie y las tarjetas siguen usando el listado
  // completo.
  const paginacion = usePaginacion(filas, { clave: filtros });
  const porCliente = datos?.por_cliente ?? [];
  const clientes = catalogos.data?.clientes ?? [];
  const estados = catalogos.data?.estados ?? [];

  const cambiar = <C extends keyof FiltrosCreditosCliente>(
    campo: C,
    valor: FiltrosCreditosCliente[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarCreditosCliente(filtros), {
        nombre: `creditos-cliente-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfCreditosCliente(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const rangoDe = (mes: string) => {
    const anio = Number(mes.slice(0, 4));
    const numero = Number(mes.slice(5, 7));

    return { desde: `${mes}-01`, hasta: fechaLocal(new Date(anio, numero, 0)) };
  };

  const meses = useMemo(() => {
    const vistos = new Map<string, number>();

    for (const fila of filas) {
      const periodo = (fila.fecha ?? '').slice(0, 7);
      if (periodo) vistos.set(periodo, (vistos.get(periodo) ?? 0) + 1);
    }

    return [...vistos.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([periodo, creditos]) => ({ periodo, creditos }));
  }, [filas]);

  const etiquetaEstado = estados.find((opcion) => opcion.valor === filtros.estado)?.etiqueta ?? '';

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Créditos por cliente</h1>
          <p className="mt-1 text-sm text-ink-500">
            Distingue el saldo pendiente de los vales del saldo total de las facturas CPE que los
            agrupan.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Estado del vale</span>
              <Select
                value={String(filtros.estado)}
                onChange={({ target }) => cambiar('estado', Number(target.value))}
                className={CONTROL}
                aria-label="Estado del vale"
              >
                {estados.map((opcion) => (
                  <option key={opcion.valor} value={String(opcion.valor)}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Placa</span>
              <input
                type="text"
                value={filtros.placa}
                onChange={(evento) => cambiar('placa', evento.target.value)}
                placeholder="ABV321"
                className={CONTROL}
                aria-label="Placa"
              />
            </label>

            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2 xl:col-span-3">
              <span className="font-medium text-ink-700">
                Cliente{' '}
                <span className="font-normal text-ink-400">
                  {catalogos.data
                    ? `(${formatNumber(clientes.length)} con créditos registrados)`
                    : ''}
                </span>
              </span>
              <Select
                value={filtros.numero_doc}
                onChange={({ target }) => cambiar('numero_doc', target.value)}
                className={CONTROL}
                aria-label="Cliente"
              >
                <option value="">TODOS</option>
                {clientes.map((cliente) => (
                  <option key={cliente.documento ?? cliente.cliente} value={cliente.documento ?? ''}>
                    {cliente.cliente} · {cliente.documento ?? 'sin documento'} (
                    {formatNumber(cliente.por_cobrar)})
                  </option>
                ))}
              </Select>
            </label>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con créditos
              </span>
              {meses.slice(0, 12).map((mes) => {
                const rango = rangoDe(mes.periodo);
                const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                return (
                  <button
                    key={mes.periodo}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                      activo
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                    )}
                    title={`${formatNumber(mes.creditos)} crédito(s)`}
                  >
                    {etiquetaMes(mes.periodo)}
                  </button>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {[
          {
            etiqueta: 'Créditos',
            valor: formatNumber(resumen?.filas ?? 0),
            detalle: `${formatNumber(resumen?.clientes ?? 0)} cliente(s)`,
          },
          {
            etiqueta: 'Total vendido',
            valor: formatCurrency(resumen?.total ?? 0),
            detalle: `descuento ${formatCurrency(resumen?.descuento ?? 0)}`,
          },
          {
            etiqueta: 'Pendiente de vales',
            valor: formatCurrency(resumen?.por_cobrar ?? 0),
            detalle: 'suma de total menos pagos de estos vales',
          },
          {
            etiqueta: 'Saldo CPE',
            valor: formatCurrency(resumen?.saldo_cpe ?? 0),
            detalle: `saldo completo de ${formatNumber(resumen?.con_pendiente ?? 0)} CPE`,
          },
          {
            etiqueta: 'Retención CPE',
            valor: formatCurrency(resumen?.retencion ?? 0),
            detalle: 'retención registrada en esos comprobantes',
          },
          {
            etiqueta: 'Cantidad',
            valor: formatNumber(resumen?.cantidad ?? 0),
            detalle: `${formatNumber(resumen?.productos ?? 0)} producto(s)`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-sm font-semibold text-ink-800">Por cliente</h2>

          {consulta.isLoading ? <Skeleton className="h-32 w-full" /> : null}

          {!consulta.isLoading ? (
            <div className="max-h-[40vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Cliente</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Créditos</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Placas</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Total</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Pendiente vales</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Saldo CPE</th>
                  </tr>
                </thead>
                <tbody>
                  {porCliente.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={6} className="px-2 py-8 text-center text-sm text-ink-500">
                        No hay créditos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {porCliente.map((fila) => (
                    <tr
                      key={fila.cliente ?? 'sin-cliente'}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-2 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.filas)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.placas)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.total)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.por_cobrar)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.pendiente)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {porCliente.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2" colSpan={3}>
                        TOTALES · {formatNumber(porCliente.length)} cliente(s)
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(resumen?.total ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(resumen?.por_cobrar ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(porCliente.reduce((total, fila) => total + fila.pendiente, 0))}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Créditos del periodo ({formatNumber(resumen?.filas ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)} ·{' '}
              {etiquetaEstado}
              {filtros.placa ? ` · placa ${filtros.placa}` : ''}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full max-lg:min-w-[49rem] table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[5.25rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[6.5rem] px-1.5 py-2">Comprobante</th>
                    <th className="px-1.5 py-2">Cliente</th>
                    <th className="px-1.5 py-2">Producto</th>
                    <th className="w-[4.5rem] px-1.5 py-2 text-right">Cant.</th>
                    <th className="w-[5rem] px-1.5 py-2 text-right">Total</th>
                    <th className="w-[5rem] px-1.5 py-2 text-right">Pend. vale</th>
                    <th className="w-[6rem] px-1.5 py-2">Placa</th>
                    <th className="w-[6rem] px-1.5 py-2">Conductor</th>
                    <th className="w-[6.5rem] px-1.5 py-2">CPE</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={10} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay créditos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila, indice) => (
                    <tr
                      key={`${fila.fecha}-${fila.serie}-${fila.vale}-${fila.placa}-${indice}`}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-1.5 py-2 text-ink-700">{formatDateValue(fila.fecha)}</td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900">
                          {fila.comprobante ?? '—'}
                        </p>
                        <Badge
                          variant={fila.fuente === 'APP' ? 'brand' : 'neutral'}
                          className="mt-0.5 px-1.5 py-0 text-[10px]"
                        >
                          {fila.fuente === 'APP' ? 'APP' : 'VALE'}
                        </Badge>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                        {fila.personal ? (
                          <p className="truncate text-[11px] text-ink-500" title={fila.personal}>
                            {fila.personal}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.producto ?? ''}>
                          {fila.producto ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {formatCurrency(fila.precio)} / u.
                          {fila.descuento !== 0 ? ` · dto ${formatCurrency(fila.descuento)}` : ''}
                        </p>
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {formatNumber(fila.cantidad)}
                      </td>
                      <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.total)}
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {formatCurrency(fila.por_cobrar)}
                        {fila.pendiente !== null ? (
                          <p className="text-[11px] text-ink-500">
                            Saldo CPE {formatCurrency(fila.pendiente)}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.placa ?? ''}>
                          {fila.placa || '—'}
                        </p>
                        {fila.kilometraje !== null ? (
                          <p className="truncate text-[11px] text-ink-500">
                            {formatNumber(fila.kilometraje)} km
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-700" title={fila.conductor ?? ''}>
                          {fila.conductor ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {fila.conductor_dni ?? ''}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-700" title={fila.cpe ?? ''}>
                          {fila.cpe || '—'}
                        </p>
                        {fila.vale ? (
                          <p className="truncate text-[11px] text-ink-500">vale {fila.vale}</p>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={4}>
                        SUMA · {formatNumber(resumen?.filas ?? 0)} crédito(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatNumber(resumen?.cantidad ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.total ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.por_cobrar ?? 0)}
                      </td>
                      <td className="px-1.5 py-2" colSpan={3}>
                        <span className="text-[11px] font-normal text-ink-500">
                          {formatNumber(resumen?.comprobantes ?? 0)} CPE · saldo{' '}
                          {formatCurrency(resumen?.saldo_cpe ?? 0)} · retención{' '}
                          {formatCurrency(resumen?.retencion ?? 0)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="vales" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          <strong>Pendiente de vales</strong> es la suma de los saldos de los vales mostrados
          (total menos pagos). <strong>Saldo CPE</strong> es el saldo completo de la factura
          asociada, que puede incluir otros vales fuera del periodo o de la placa filtrada; por eso
          puede ser mayor que las ventas visibles. Se informa una sola vez por CPE. La retención es
          otro importe del comprobante. El estado filtra por facturación y pago.
        </span>
      </p>
    </div>
  );
}
